import type {CallRestrictionRule, ScopeId, ScopeMode} from '../../store/features/CallRestrictions/callRestrictionsTypes';

export type ScopeKind = 'company' | 'group' | 'person' | 'number';

export interface ScopeOption {
    scopeId: ScopeId;
    name: string;
    sub?: string;
    mode: ScopeMode | null;
}

export const scopeKindOf = (scopeId: ScopeId): ScopeKind =>
    scopeId.startsWith('group-') ? 'group'
        : scopeId.startsWith('user-') ? 'person'
            : scopeId.startsWith('number-') ? 'number'
                : 'company';

export type PriceLimitInput = {value: number | null; error?: undefined} | {value?: undefined; error: string};

/**
 * Reads a "per minute" field. Empty means no limit of its own. A value above
 * `max` is refused before asking the backend, with the same wording as its
 * PRICE_LIMIT_ABOVE_ALLOWED answer.
 */
export const parsePriceLimit = (input: string, max: number | null, currency: string): PriceLimitInput => {
    const trimmed = input.trim();
    if (!trimmed) return {value: null};
    if (!/^\d+([.,]\d{1,4})?$/.test(trimmed)) return {error: 'Enter an amount like 1.50.'};
    const value = Number(trimmed.replace(',', '.'));
    if (max !== null && value > max) return {error: aboveAllowedMessage(max, currency)};
    return {value};
};

export const aboveAllowedMessage = (max: number | null, currency: string): string =>
    max === null
        ? 'This is more than PhoneHQ allows for your company. Enter a lower limit.'
        : `PhoneHQ allows up to ${perMinute(max, currency)} for your company. Enter a lower limit.`;

/** `1.50 PLN / min`. */
export const perMinute = (value: number, currency: string): string =>
    `${value.toFixed(2)}${currency ? ` ${currency}` : ''} / min`;

/** The backend's rule label is the note when there is one, otherwise the destination name: use it as a name only then. */
export const ruleName = (rule: Pick<CallRestrictionRule, 'note' | 'label'>): string | undefined =>
    rule.note ? undefined : rule.label || undefined;
